/**
 * The virtualised sidebar tree (spec M0-F4 §7.4, S3–S8).
 *
 * One workspace in this account holds 404 boards `[fl]`, so the tree virtualises above
 * `VIRTUALISE_ABOVE` nodes and keeps roughly a screenful of rows mounted (S5). Rows are a
 * `role="tree"` / `treeitem` structure with roving focus: ↑↓ move, → expands, ← collapses,
 * Enter opens (S7).
 *
 * **P-171 — the roving focus is keyed on the row, not on its index.** `focusIndex` was an index
 * into `rows`, and expanding a folder *inserts* rows: every index below it shifted, the
 * "scroll the selection into view" effect then re-pointed the focus at the selected board, and
 * the very next key acted on a row the user had never moved to — `→` then `←` did not collapse
 * what had just been opened. The focus is now the row's own key (`FlatRow.key`, which exists
 * because a folder and a board may share an id) and the index is re-derived from it, so a list
 * that grows under the focus cannot move it. The focused row is also the only tabbable one
 * (roving `tabIndex`), which is what stops React's idea of the focus and the browser's from
 * diverging silently.
 *
 * **P-161 — S8's half of the drag.** dnd-kit, like the non-table views (`views/shared/dnd.ts`),
 * because the sidebar needs the keyboard sensor S7's accessibility claim implies: Space starts
 * a drag, the arrows move it, Space drops it. `tree-dnd.ts` owns what a drop *means*.
 */
import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  pointerWithin,
  useDroppable,
  useSensor,
  useSensors,
  type CollisionDetection,
  type DragEndEvent,
  type DragMoveEvent,
  type DragStartEvent,
} from '@dnd-kit/core';
import { SortableContext, sortableKeyboardCoordinates, useSortable } from '@dnd-kit/sortable';
import { EntityIcon, IconButton, SidebarRow, boardEntityKind, dimensions, icons } from '@elizmonday/ui';
import { useVirtualizer } from '@tanstack/react-virtual';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { ROOT_DROP_KEY, bandFor, planSidebarDrop, planUnnestFolder, type DropBand } from './tree-dnd';
import { entityUrl } from '../../modules/shell/urls';
import type { FlatRow, TreeNode } from '../../modules/shell/tree';
import { SidebarRowMenu, type SidebarRowTarget } from '../../modules/boards/SidebarRowMenu';
import { useFavorites } from '../../modules/boards/favorites';
import { useTRPCClient } from '../../lib/trpc';
import { useBoardInvalidation, type BoardKind } from '../../modules/boards/hooks';

/** Below this the DOM is cheap enough that virtualising only costs a scroll container. */
export const VIRTUALISE_ABOVE = 200;

export interface SidebarTreeProps {
  rows: FlatRow[];
  expandedFolderIds: ReadonlySet<string>;
  onToggleFolder: (folderId: string) => void;
  /** The board / doc / dashboard the route is currently on. */
  selectedId: string | null;
  emptyLabel: string;
  /** Needed by the row menu, which can move a board or create inside the same workspace. */
  workspaceId?: string | null;
  /** The folder row menu's `Collapse all folders` (`[c:sandbox-folder-menu]`). */
  onCollapseAllFolders?: () => void;
}

function iconFor(node: TreeNode) {
  if (node.kind === 'folder') return null;
  const leaf = node.leaf;
  if (leaf.type === 'doc') return <EntityIcon kind="doc" />;
  if (leaf.type === 'dashboard') return <EntityIcon kind="dashboard" />;
  return <EntityIcon kind={boardEntityKind(leaf.kind)} />;
}

/**
 * One row, and the drag surface around it (**P-161**).
 *
 * dnd-kit's `attributes` land on the **wrapper**, never on the `SidebarRow` itself: the row is
 * the `treeitem` and keeps its ARIA, while the wrapper is the sortable — which is what gives a
 * row the `role="button" aria-roledescription="sortable"` a screen reader needs to announce a
 * drag, and the keyboard sensor the `Space · arrows · Space` gesture S7 implies.
 *
 * It is declared **here**, at module scope, and not inside `SidebarTree`: a component defined in
 * another component's body is a new type on every render, so React unmounts and remounts every
 * row whenever the tree re-renders — which is exactly what a drag does, sixty times a second,
 * and dnd-kit loses the node it is dragging the first time it happens.
 */
function SortableRow({
  row,
  index,
  style,
  band,
  focused,
  selectedId,
  expandedFolderIds,
  favoriteKeys,
  onToggleFolder,
  onActivate,
  onFocusRow,
  onMenu,
  onToggleFavorite,
  targetOf,
}: {
  row: FlatRow;
  index: number;
  style?: React.CSSProperties;
  /** P-280: the band the indicator is painting on **this** row, if any. */
  band: DropBand | null;
  focused: boolean;
  selectedId: string | null;
  expandedFolderIds: ReadonlySet<string>;
  favoriteKeys: ReadonlySet<string>;
  onToggleFolder: (folderId: string) => void;
  onActivate: (row: FlatRow) => void;
  onFocusRow: (key: string) => void;
  onMenu: (target: SidebarRowTarget, x: number, y: number) => void;
  onToggleFavorite: (target: SidebarRowTarget, isFavorite: boolean) => void;
  targetOf: (node: TreeNode) => SidebarRowTarget;
}) {
  const node = row.node;
  const isFolder = node.kind === 'folder';
  const selected = !isFolder && node.id === selectedId;
  const favourite = isFolder
    ? favoriteKeys.has(`folder:${node.id}`)
    : node.leaf.isFavorite || favoriteKeys.has(`${node.leaf.type}:${node.id}`);
  const sortable = useSortable({ id: row.key });

  return (
    <div
      ref={sortable.setNodeRef}
      data-row-index={index}
      data-dragging={sortable.isDragging || undefined}
      /*
       * **P-280**: the indicator and the write are the same value. `band` comes from the very
       * `{ overKey, band }` the drop handler will apply, so the line the user sees above or
       * below a row *is* the insertion that happens, and the ring on a folder *is* the nesting.
       */
      data-drop-band={band ?? undefined}
      className={`ez-pane__tree-row-wrap${style ? ' ez-pane__tree-row' : ''}`}
      style={{
        ...style,
        opacity: sortable.isDragging ? 0.4 : undefined,
      }}
      {...sortable.attributes}
      {...sortable.listeners}
      /* P-171: roving tabindex — the focused row is the one Tab reaches, and every row is
         focusable at all, which is what the `.focus()` above needs to mean anything. */
      tabIndex={focused ? 0 : -1}
    >
      <SidebarRow
        label={node.name}
        level={row.level}
        selected={selected}
        {...(isFolder ? { expanded: expandedFolderIds.has(node.id) } : {})}
        onToggle={isFolder ? () => onToggleFolder(node.id) : undefined}
        onClick={(event) => {
          /*
           * P-171: the pointer leaves the roving focus **on the row it clicked**, in the DOM
           * as well as in the state. dnd-kit's pointer sensor calls `preventDefault` on the
           * press it might turn into a drag, and a prevented `mousedown` never focuses
           * anything — so the row is focused here, explicitly, before anything else.
           */
          (event.currentTarget as HTMLElement | null)?.focus?.();
          onFocusRow(row.key);
          onActivate(row);
        }}
        /* Space starts a **drag** here (dnd-kit's keyboard sensor); Enter still opens, which
           is what S7 documents for the tree. */
        activateOnSpace={false}
        tabIndex={focused ? 0 : -1}
        icon={iconFor(node)}
        color={isFolder ? node.folder.color : null}
        fontWeight={isFolder ? (node.folder.fontWeight ?? null) : null}
        onContextMenu={(event) => {
          event.preventDefault();
          onMenu(targetOf(node), event.clientX, event.clientY);
        }}
        actions={
          <>
            <IconButton
              icon={icons.moreActions}
              ariaLabel={`${node.name} options`}
              size="xs"
              kind="tertiary"
              /**
               * Kind-scoped, like `tree-row-…`: a folder and a board **may share an id**
               * (`tree.ts`'s `FlatRow.key` exists for exactly that reason), and an
               * id-only testid resolved to two elements — one of them hidden — which is
               * how the first e2e run "clicked" a folder's menu while hovering a board.
               */
              data-testid={`tree-menu-${node.kind}-${node.id}`}
              onClick={(event) => {
                event.stopPropagation();
                const box = (event.currentTarget as HTMLElement).getBoundingClientRect();
                onMenu(targetOf(node), box.right + 4, box.top);
              }}
            />
            <IconButton
              icon={favourite ? icons.favoriteFilled : icons.favorite}
              ariaLabel={favourite ? `Remove ${node.name} from favorites` : `Add ${node.name} to favorites`}
              size="xs"
              kind="tertiary"
              active={favourite}
              data-testid={`tree-star-${node.kind}-${node.id}`}
              onClick={(event) => {
                event.stopPropagation();
                onToggleFavorite(targetOf(node), favourite);
              }}
            />
          </>
        }
        data-testid={`tree-row-${node.kind}-${node.id}`}
      />
    </div>
  );
}

/**
 * **P-279 — the workspace's own level, as a drop target.**
 *
 * Every drop target in this tree used to be a *row*, and a row's meaning is "into or beside this
 * thing"; the workspace itself had none, so a folder dragged into another folder could only be
 * dragged deeper. The insertion band above a root row is one way back out (`tree-dnd.ts`); this
 * is the other, and it is the one monday shows as "drop below everything": the empty space under
 * the last row, which is the workspace's top level at the end.
 *
 * It only claims space while a drag is in flight — an idle sidebar must not have an invisible
 * strip under the tree swallowing clicks.
 */
function RootDropZone({ active, over }: { active: boolean; over: boolean }) {
  const droppable = useDroppable({ id: ROOT_DROP_KEY, disabled: !active });
  return (
    <div
      ref={droppable.setNodeRef}
      className="ez-pane__tree-root-drop"
      data-active={active || undefined}
      data-over={over || undefined}
      data-testid="sidebar-root-drop"
      aria-hidden="true"
    />
  );
}

export function SidebarTree({
  rows,
  expandedFolderIds,
  onToggleFolder,
  selectedId,
  emptyLabel,
  workspaceId = null,
  onCollapseAllFolders,
}: SidebarTreeProps) {
  const navigate = useNavigate();
  const scrollRef = useRef<HTMLDivElement>(null);
  /**
   * P-171: the **row**, not its index. `null` means "the first row", which is what an index of
   * 0 used to mean and the only thing it got right.
   */
  const [focusKey, setFocusKey] = useState<string | null>(null);
  const focusIndex = useMemo(() => {
    const index = rows.findIndex((row) => row.key === focusKey);
    return index < 0 ? 0 : index;
  }, [rows, focusKey]);
  /** The dragged row's key while a drag is in flight — the `DragOverlay`'s subject (P-161). */
  const [dragKey, setDragKey] = useState<string | null>(null);
  /**
   * **P-280**: where the drop will land, decided **while the pointer moves** and kept until the
   * release. It is both what the indicator paints and what `applyDrop` writes, which is the
   * whole point of the card: the two used to be computed separately and disagreed.
   */
  const [dropTarget, setDropTarget] = useState<{ overKey: string; band: DropBand } | null>(null);
  /**
   * B10: the row `···` menu and the star. Both were stubs — the star was `disabled` with
   * "B1 owns the mutation" long after B1 shipped `favorites.*`, and there was no menu at all.
   */
  const [menu, setMenu] = useState<{ target: SidebarRowTarget; x: number; y: number } | null>(null);
  const client = useTRPCClient();
  const favorites = useFavorites();
  const invalidate = useBoardInvalidation();

  const favoriteKeys = useMemo(
    () => new Set((favorites.data ?? []).map((row) => `${row.entityType}:${String(row.entityId)}`)),
    [favorites.data],
  );

  const toggleFavorite = useCallback(
    async (target: SidebarRowTarget, isFavorite: boolean) => {
      const input = { entityType: target.kind, entityId: target.id } as const;
      await (isFavorite ? client.favorites.remove.mutate(input) : client.favorites.add.mutate(input));
      await favorites.refetch();
      await invalidate();
    },
    [client, favorites, invalidate],
  );

  /** Runs a plan through B1's mutations, then re-reads the tree whatever happened. */
  const writePlan = useCallback(
    async (plan: ReturnType<typeof planSidebarDrop>) => {
      if (!plan) return;
      try {
        if (plan.kind === 'move-board') {
          /*
           * **P-281**: the same mutation, and now with the ordering key an insertion band
           * always meant. `position` is omitted when the gap could not be measured, and
           * `boards.move` then leaves the board's stored order alone — a re-file, which is
           * what this call has always been.
           */
          await client.boards.move.mutate({
            id: plan.boardId,
            folderId: plan.folderId,
            ...(plan.position === undefined ? {} : { position: plan.position }),
          });
        } else if (plan.kind === 'reorder-board') {
          // A board dropped between two rows of the folder it is already in (P-281).
          await client.boards.move.mutate({ id: plan.boardId, position: plan.position });
        } else if (plan.kind === 'move-folder') {
          await client.folders.update.mutate({
            id: plan.folderId,
            parentFolderId: plan.parentFolderId,
            // An insertion band re-parents **and** places; a drop on a folder's body only
            // re-parents, and `folders.update` leaves an omitted field alone.
            ...(plan.position === undefined ? {} : { position: plan.position }),
          });
        } else {
          await client.folders.update.mutate({ id: plan.folderId, position: plan.position });
        }
      } finally {
        // Whatever happened, the tree is re-read: a refused move must not leave the row where
        // the drag preview left it.
        await invalidate();
      }
    },
    [client, invalidate],
  );

  const targetOf = useCallback(
    (node: TreeNode): SidebarRowTarget =>
      node.kind === 'folder'
        ? { kind: 'folder', id: node.id, name: node.name, workspaceId }
        : {
            kind: node.leaf.type,
            id: node.leaf.id,
            name: node.name,
            boardKind: node.leaf.kind as BoardKind,
            workspaceId,
          },
    [workspaceId],
  );

  const virtualise = rows.length > VIRTUALISE_ABOVE;
  const virtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => dimensions.sidebarRowHeight,
    overscan: 8,
    enabled: virtualise,
  });

  const selectedIndex = useMemo(
    () => rows.findIndex((row) => row.node.kind === 'leaf' && row.node.id === selectedId),
    [rows, selectedId],
  );

  /*
   * S2/S4: navigating to a board scrolls it into view — and moves the roving focus with it.
   *
   * **P-171**: only when the **selection** changes. It used to run on `selectedIndex`, which
   * changes whenever the *list* changes — expanding a folder above the selected board shifted
   * its index, this effect fired, and the focus jumped off the folder the user had just opened
   * onto the selected board, so the next `←` collapsed nothing.
   */
  const lastSelectedId = useRef<string | null>(null);
  useEffect(() => {
    if (selectedIndex < 0) return;
    const selectionChanged = lastSelectedId.current !== selectedId;
    lastSelectedId.current = selectedId;
    if (selectionChanged) setFocusKey(rows[selectedIndex]?.key ?? null);
    if (virtualise) {
      virtualizer.scrollToIndex(selectedIndex, { align: 'auto' });
      return;
    }
    const element = scrollRef.current?.querySelector(`[data-row-index="${selectedIndex}"]`);
    // jsdom has no layout, so `scrollIntoView` is absent there; the behaviour is covered by
    // the Playwright suite instead.
    if (element instanceof HTMLElement) element.scrollIntoView?.({ block: 'nearest' });
  }, [selectedIndex, selectedId, rows, virtualise, virtualizer]);

  const activate = useCallback(
    (row: FlatRow) => {
      if (row.node.kind === 'folder') {
        onToggleFolder(row.node.id);
        return;
      }
      navigate(entityUrl({ id: row.node.leaf.id, type: row.node.leaf.type }));
    },
    [navigate, onToggleFolder],
  );

  const onKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      const row = rows[focusIndex];
      if (!row) return;

      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault();
          setFocusKey(rows[Math.min(focusIndex + 1, rows.length - 1)]?.key ?? null);
          break;
        case 'ArrowUp':
          event.preventDefault();
          setFocusKey(rows[Math.max(focusIndex - 1, 0)]?.key ?? null);
          break;
        case 'ArrowRight':
          if (row.node.kind === 'folder' && !expandedFolderIds.has(row.node.id)) {
            event.preventDefault();
            onToggleFolder(row.node.id);
          }
          break;
        case 'ArrowLeft':
          /*
           * **P-279 · the keyboard equivalent of dragging a folder back out.** `Shift+←` on a
           * nested folder moves it to the workspace's own top level — the same write the root
           * insertion band performs, for the hands that never touch the pointer. Plain `←`
           * still collapses, which is S7's contract and is left alone.
           *
           * ASSUMED: no capture shows monday's own shortcut for this (the folder menu and the
           * drag are the two paths it documents), so the modifier is this product's choice and
           * mirrors `Shift+→`-style "do the structural version of this key" elsewhere.
           */
          if (event.shiftKey) {
            const plan = planUnnestFolder(rows, row.key);
            if (plan) {
              event.preventDefault();
              void writePlan(plan);
            }
            break;
          }
          if (row.node.kind === 'folder' && expandedFolderIds.has(row.node.id)) {
            event.preventDefault();
            onToggleFolder(row.node.id);
          }
          break;
        case 'Enter':
          event.preventDefault();
          activate(row);
          break;
        default:
          break;
      }
    },
    [rows, focusIndex, expandedFolderIds, onToggleFolder, activate, writePlan],
  );

  /*
   * Roving focus: exactly one row is tabbable (`tabIndex={0}` below) and it follows the arrow
   * keys. **P-171**: the element that takes the focus is the **row** — `.ez-sidebar-row`, which
   * carries the `treeitem` role and the row's own test id — and not the positioning wrapper the
   * effect used to call `.focus()` on, which had no `tabIndex` at all and therefore silently
   * ignored it, leaving the DOM focus behind wherever the last click had put it.
   */
  useEffect(() => {
    const wrapper = scrollRef.current?.querySelector<HTMLElement>(`[data-row-index="${focusIndex}"]`);
    const element = wrapper?.querySelector<HTMLElement>('.ez-sidebar-row') ?? wrapper;
    if (element && scrollRef.current?.contains(document.activeElement)) element.focus();
  }, [focusIndex, rows.length, focusKey]);

  /* ── S8's gesture (P-161) ─────────────────────────────────────────────────
   *
   * The same two sensors and the same 5 px activation as the views' own drag
   * (`views/shared/dnd.ts`): a press that never moves has to reach the row's click, because a
   * sidebar row is how a board is opened.
   *
   * **P-280**: `pointerWithin` **first**, `closestCenter` only as the fallback. The band a drop
   * lands in is measured from the pointer against the row's own box, so the row the pointer is
   * literally inside has to be the row that band is measured against — `closestCenter` answers
   * with the nearest *centre*, which near a row boundary is regularly the other row, and the
   * two disagreeing is how a "between" gesture became an "into" write. `pointerWithin` returns
   * nothing for the keyboard sensor, which has no pointer, and then `closestCenter` is exactly
   * the right answer.
   */
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    /*
     * `sortableKeyboardCoordinates` moves the drag to the **next row**, which is what a tree
     * means by "down". dnd-kit's default getter steps 25 px, and a sidebar row is 28 px high,
     * so Space · ArrowDown · Space would land the drag back on the row it started from.
     */
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const collisionDetection = useCallback<CollisionDetection>((args) => {
    const inside = pointerWithin(args);
    return inside.length > 0 ? inside : closestCenter(args);
  }, []);

  /**
   * **P-280.** The band is decided here, on every move, from the pointer against the row the
   * pointer is in — and stored. `applyDrop` then writes *that* decision rather than working the
   * answer out again from `over` alone, which is what let the indicator and the mutation drift
   * apart. A keyboard drag has no pointer, so the dragged row's own translated box stands in
   * for one: its centre lands in the middle of the row it has been walked onto.
   */
  const trackBand = useCallback(
    (event: DragMoveEvent) => {
      const over = event.over;
      if (!over) {
        setDropTarget(null);
        return;
      }
      const overKey = String(over.id);
      if (overKey === ROOT_DROP_KEY) {
        setDropTarget({ overKey, band: 'after' });
        return;
      }
      const overRow = rows.find((row) => row.key === overKey);
      if (!overRow) {
        setDropTarget(null);
        return;
      }
      const activator = event.activatorEvent as Partial<PointerEvent> | undefined;
      const dragged = event.active.rect.current.translated;
      const pointerY =
        typeof activator?.clientY === 'number'
          ? activator.clientY + event.delta.y
          : dragged
            ? dragged.top + dragged.height / 2
            : over.rect.top + over.rect.height / 2;
      setDropTarget({
        overKey,
        band: bandFor(pointerY, over.rect, { canNest: overRow.node.kind === 'folder' }),
      });
    },
    [rows],
  );

  const applyDrop = useCallback(
    async (event: DragEndEvent) => {
      const target = dropTarget;
      setDragKey(null);
      setDropTarget(null);
      const overId = target?.overKey ?? event.over?.id;
      if (overId === undefined || overId === null) return;
      await writePlan(planSidebarDrop(rows, String(event.active.id), String(overId), target?.band ?? 'into'));
    },
    [rows, dropTarget, writePlan],
  );

  const onDragStart = useCallback((event: DragStartEvent) => {
    setDragKey(String(event.active.id));
    setDropTarget(null);
  }, []);
  const draggedRow = dragKey === null ? null : (rows.find((row) => row.key === dragKey) ?? null);

  function renderRow(row: FlatRow, index: number, style?: React.CSSProperties) {
    return (
      <SortableRow
        key={row.key}
        row={row}
        index={index}
        {...(style ? { style } : {})}
        band={dropTarget?.overKey === row.key ? dropTarget.band : null}
        focused={index === focusIndex}
        selectedId={selectedId}
        expandedFolderIds={expandedFolderIds}
        favoriteKeys={favoriteKeys}
        onToggleFolder={onToggleFolder}
        onActivate={activate}
        onFocusRow={setFocusKey}
        onMenu={(target, x, y) => setMenu({ target, x, y })}
        onToggleFavorite={(target, isFavorite) => void toggleFavorite(target, isFavorite)}
        targetOf={targetOf}
      />
    );
  }

  const menuHost = menu ? (
    <SidebarRowMenu
      target={menu.target}
      x={menu.x}
      y={menu.y}
      onClose={() => setMenu(null)}
      {...(onCollapseAllFolders ? { onCollapseAllFolders } : {})}
    />
  ) : null;

  if (rows.length === 0) {
    return (
      <div className="ez-pane__tree" data-testid="sidebar-tree">
        <p className="ez-pane__empty">{emptyLabel}</p>
        {menuHost}
      </div>
    );
  }

  return (
    <div ref={scrollRef} className="ez-pane__tree" data-testid="sidebar-tree" data-virtualised={virtualise}>
      <DndContext
        sensors={sensors}
        collisionDetection={collisionDetection}
        onDragStart={onDragStart}
        onDragMove={trackBand}
        onDragOver={trackBand}
        onDragEnd={(event) => void applyDrop(event)}
        onDragCancel={() => {
          setDragKey(null);
          setDropTarget(null);
        }}
      >
        {/*
          * **P-280**: no sorting strategy. `verticalListSortingStrategy` slid the rows apart to
          * open a gap under the pointer — an insertion indicator in everything but name, painted
          * by a library that knew nothing about what this tree's drop actually writes. The gap
          * said "between these two rows" while the drop nested the folder into one of them. The
          * indicator is now `data-drop-band`, drawn from the decision itself, and the rows stay
          * where they are.
          */}
        <SortableContext items={rows.map((row) => row.key)} strategy={() => null}>
          <div
            role="tree"
            aria-label="Workspace content"
            /* P-163: the tree is the keyboard surface (↑↓→←/Enter) and it is addressable. */
            data-testid="sidebar-tree-keys"
            tabIndex={0}
            onKeyDown={onKeyDown}
            className="ez-pane__tree-inner"
            style={virtualise ? { height: virtualizer.getTotalSize() } : undefined}
          >
            {virtualise
              ? virtualizer.getVirtualItems().map((item) => {
                  const row = rows[item.index];
                  if (!row) return null;
                  return renderRow(row, item.index, {
                    transform: `translateY(${item.start}px)`,
                    height: item.size,
                  });
                })
              : rows.map((row, index) => renderRow(row, index))}
          </div>
        </SortableContext>
        <RootDropZone active={dragKey !== null} over={dropTarget?.overKey === ROOT_DROP_KEY} />
        {/* S8's other half: the drag preview is the row itself, not the browser's screenshot. */}
        <DragOverlay dropAnimation={null}>
          {draggedRow ? (
            <div className="ez-sidebar-row ez-sidebar-row--drag-preview" data-testid="sidebar-drag-preview">
              {draggedRow.node.name}
            </div>
          ) : null}
        </DragOverlay>
      </DndContext>
      {menuHost}
    </div>
  );
}
